import React, { createContext, FC, ReactNode, useContext, useEffect, useReducer } from 'react'
import {
  FamilyMember,
  Fetch,
  FetchState,
  HttpRequest,
  TicketRight,
  YearlyLowIncome,
} from '../api-clients/api-types'
import { useApiOnLoad } from '../api-clients/client-hooks'
import { getFamilyMembers } from '../api-clients/eba-clients'
import { useEmployee } from './employee-context'

interface FamilyMembersContextInterface {
  familyMembersResponse: Fetch<Array<FamilyMember>>
  fetchFamilyMembers: (request: HttpRequest) => void
  dispatch: React.Dispatch<Action>
}

enum ActionState {
  UPDATE,
  UPDATE_ALL,
  UPDATE_ONE,
  UPDATE_ABSTAIN_FROM_TICKET,
  UPDATE_LOW_INCOME,
  UPDATE_TICKET_RIGHTS,
  UPDATE_TICKET_RIGHT,
  UPDATE_DRAFT_TICKET_RIGHTS,
}

type Action =
  | { type: ActionState.UPDATE; newResponse: Fetch<Array<FamilyMember>> }
  | { type: ActionState.UPDATE_ALL; familyMembers: FamilyMember[] }
  | { type: ActionState.UPDATE_ONE; familyMember: FamilyMember }
  | { type: ActionState.UPDATE_ABSTAIN_FROM_TICKET; familyMember: FamilyMember }
  | { type: ActionState.UPDATE_LOW_INCOME; familyMemberId: string; lowIncome: YearlyLowIncome }
  | { type: ActionState.UPDATE_TICKET_RIGHTS; ticketRights: TicketRight[] }
  | { type: ActionState.UPDATE_TICKET_RIGHT; ticketRight: TicketRight }
  | { type: ActionState.UPDATE_DRAFT_TICKET_RIGHTS; draftTicketRights: TicketRight[] }

const Reducer = (state: Fetch<Array<FamilyMember>>, action: Action) => {
  switch (action.type) {
    case ActionState.UPDATE:
      return action.newResponse
    case ActionState.UPDATE_ALL:
      return { ...state, data: action.familyMembers }
    case ActionState.UPDATE_ONE:
      if (state.status === FetchState.SUCCESS) {
        const updatedFamilyMembers = state.data.filter((it) => it.id !== action.familyMember.id)
        updatedFamilyMembers.push(action.familyMember)
        return { ...state, data: updatedFamilyMembers }
      }
      return state
    case ActionState.UPDATE_ABSTAIN_FROM_TICKET:
      if (state.status === FetchState.SUCCESS) {
        const updatedFamilyMembers = state.data.map((it) =>
          it.id === action.familyMember.id
            ? { ...it, abstainFromTicket: action.familyMember.abstainFromTicket }
            : it
        )
        return { ...state, data: updatedFamilyMembers }
      }
      return state
    case ActionState.UPDATE_LOW_INCOME:
      if (state.status === FetchState.SUCCESS) {
        const updatedFamilyMembers = state.data.map((it) => {
          const newLowIncomes =
            it.lowIncomes?.filter((lowIncome) => lowIncome.year !== action.lowIncome.year) || []
          newLowIncomes.push(action.lowIncome)
          return it.id === action.familyMemberId ? { ...it, lowIncomes: newLowIncomes } : it
        })
        return { ...state, data: updatedFamilyMembers }
      }
      return state
    case ActionState.UPDATE_TICKET_RIGHTS:
      if (state.status === FetchState.SUCCESS) {
        const updatedFamilyMembers = state.data.map((familyMember) => {
          const newTicketRights = action.ticketRights.filter(
            (tr) => tr.ticketRightsHolder.ticketHolderCode === familyMember.ticketHolderCode
          )
          return { ...familyMember, ticketRights: newTicketRights }
        })
        return { ...state, data: updatedFamilyMembers }
      }
      return state
    case ActionState.UPDATE_TICKET_RIGHT:
      if (state.status === FetchState.SUCCESS) {
        const updatedFamilyMembers = state.data.map((familyMember) => {
          const oldTicketRight = familyMember.ticketRights?.find(
            (tr) => tr.ticketrightId === action.ticketRight.ticketrightId
          )
          if (oldTicketRight) {
            const ticketRights = familyMember.ticketRights?.filter(
              (tr) => tr.ticketrightId !== action.ticketRight.ticketrightId
            )
            ticketRights?.push(action.ticketRight)
            return { ...familyMember, ticketRights }
          }
          return familyMember
        })
        return { ...state, data: updatedFamilyMembers }
      }
      return state
    case ActionState.UPDATE_DRAFT_TICKET_RIGHTS:
      if (state.status === FetchState.SUCCESS) {
        const updatedFamilyMembers = state.data.map((familyMember) => {
          const newTicketRights = action.draftTicketRights.filter(
            (tr) => tr.ticketRightsHolder.ticketHolderCode === familyMember.ticketHolderCode
          )
          return { ...familyMember, draftTicketRights: newTicketRights }
        })
        return { ...state, data: updatedFamilyMembers }
      }
      return state
    default:
      return state
  }
}

const FamilyMemberContext = createContext<FamilyMembersContextInterface>({} as FamilyMembersContextInterface)

export const useFamilyMembers = () => {
  const context = useContext(FamilyMemberContext)
  return {
    familyMembersResponse: context?.familyMembersResponse,
    fetchFamilyMembers: context?.fetchFamilyMembers,
    setResponse: (newResponse: Fetch<Array<FamilyMember>>) => {
      context?.dispatch({ type: ActionState.UPDATE, newResponse })
    },
    setFamilyMembers: (familyMembers: FamilyMember[]) => {
      context?.dispatch({ type: ActionState.UPDATE_ALL, familyMembers })
    },
    setFamilyMember: (familyMember: FamilyMember) => {
      context?.dispatch({ type: ActionState.UPDATE_ONE, familyMember })
    },
    setAbstainFamilyMember: (familyMember: FamilyMember) => {
      context?.dispatch({ type: ActionState.UPDATE_ABSTAIN_FROM_TICKET, familyMember })
    },
    setLowIncomeForFamilyMember: (familyMemberId: string, lowIncome: YearlyLowIncome) => {
      context?.dispatch({ type: ActionState.UPDATE_LOW_INCOME, familyMemberId, lowIncome })
    },
    setTicketRights: (ticketRights: TicketRight[]) => {
      context?.dispatch({ type: ActionState.UPDATE_TICKET_RIGHTS, ticketRights })
    },
    setTicketRight: (ticketRight: TicketRight) => {
      context?.dispatch({ type: ActionState.UPDATE_TICKET_RIGHT, ticketRight })
    },
    setDraftTicketRights: (draftTicketRights: TicketRight[]) => {
      context?.dispatch({ type: ActionState.UPDATE_DRAFT_TICKET_RIGHTS, draftTicketRights })
    },
  }
}

interface FamilyMembersProviderProps {
  children: ReactNode
}
export const FamilyMemberProvider: FC<FamilyMembersProviderProps> = ({ children }) => {
  const { employee } = useEmployee()
  const [familyMembersResponse, fetchFamilyMembers] = useApiOnLoad(getFamilyMembers, {
    id: employee.id,
  })
  const [responseState, dispatch] = useReducer(Reducer, familyMembersResponse)

  useEffect(() => {
    dispatch({ type: ActionState.UPDATE, newResponse: familyMembersResponse })
  }, [familyMembersResponse])

  return (
    <FamilyMemberContext.Provider value={{ familyMembersResponse: responseState, fetchFamilyMembers, dispatch }}>
      {children}
    </FamilyMemberContext.Provider>
  )
}
